import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay, faRotateRight, faSpinner } from '@fortawesome/free-solid-svg-icons'

import { ForecastDateRangePicker } from '../ForecastDateRangePicker'
import { EntitySearch } from '../../EntitySearch'
import { TimeRangeArgsFields } from './TimeRangeArgsFields'

import type {
  NearbyTarget,
  OperationInputs,
  OperatorDef,
  OperatorId,
  ForecastType,
} from '../operations'
import {
  DEFAULT_OPERATION_INPUTS,
  NEARBY_TARGET_OPTIONS,
  parseStepPointRef,
} from '../operations'

import {
  OPERATOR_ICON,
  areOperatorArgsComplete,
  initialInputsForOperator,
  defaultForecastDates,
  defaultTimeDates,
} from './mapOperatorArgs'

export type OperatorPopupDestinationOption = {
  id: string
  label: string
  group?: string
  keywords?: string
}

export type OperatorPopupVariant = 'entity' | 'result'

type UseOperatorPopupStateOptions = {
  operators: OperatorDef[]
  preferredDestinationRef?: string
  operationResultOperatorId?: OperatorId | null
  operationRunning?: boolean
  /** Marker selected (entity) or focused (result). */
  isActive: boolean
  /** Entity-only: bump to re-open popup like a map click. */
  openPopupToken?: number
  /** Invoked when an operator should run (caller binds entity/point target). */
  onRun?: (op: OperatorDef, inputs: OperationInputs) => void
}

export function useOperatorPopupState({
  operators,
  preferredDestinationRef = '',
  operationResultOperatorId = null,
  operationRunning = false,
  isActive,
  openPopupToken,
  onRun,
}: UseOperatorPopupStateOptions) {
  const [activeOperatorId, setActiveOperatorId] = useState<OperatorId | null>(null)
  const [popupDismissed, setPopupDismissed] = useState(false)
  const [draftInputs, setDraftInputs] = useState<OperationInputs>(() => ({
    ...DEFAULT_OPERATION_INPUTS,
    ...defaultForecastDates(),
    ...defaultTimeDates(),
  }))
  const draftInputsRef = useRef(draftInputs)
  const activeOperatorRef = useRef<OperatorDef | null>(null)
  const lastAutoRunKeyRef = useRef<string>('')
  const wasActiveRef = useRef(false)

  useEffect(() => {
    draftInputsRef.current = draftInputs
  }, [draftInputs])

  const dismissPopup = useCallback(() => {
    setPopupDismissed(true)
    setActiveOperatorId(null)
    lastAutoRunKeyRef.current = ''
  }, [])

  const revealPopup = useCallback(() => {
    setPopupDismissed(false)
  }, [])

  useEffect(() => {
    if (openPopupToken === undefined) return
    setPopupDismissed(false)
  }, [openPopupToken])

  useEffect(() => {
    if (isActive && !wasActiveRef.current) setPopupDismissed(false)
    if (!isActive) {
      setPopupDismissed(false)
      setActiveOperatorId(null)
      lastAutoRunKeyRef.current = ''
    }
    wasActiveRef.current = isActive
  }, [isActive])

  const activeOperator = useMemo(
    () => operators.find((op) => op.id === activeOperatorId) ?? null,
    [operators, activeOperatorId],
  )

  useEffect(() => {
    activeOperatorRef.current = activeOperator
  }, [activeOperator])

  const runOperator = useCallback(
    (op: OperatorDef, inputs: OperationInputs) => {
      if (!onRun || operationRunning) return
      if (!areOperatorArgsComplete(op, inputs)) return
      const key = `${op.id}:${JSON.stringify(inputs)}`
      if (lastAutoRunKeyRef.current === key) return
      lastAutoRunKeyRef.current = key
      onRun(op, inputs)
    },
    [onRun, operationRunning],
  )

  const selectOperator = useCallback(
    (op: OperatorDef) => {
      const nextInputs = initialInputsForOperator(op, preferredDestinationRef)
      draftInputsRef.current = nextInputs
      setPopupDismissed(false)
      setActiveOperatorId(op.id)
      setDraftInputs(nextInputs)
      lastAutoRunKeyRef.current = ''
      // Only auto-run operators with no arguments (Contains, license check, …).
      if (op.args.length === 0) {
        runOperator(op, nextInputs)
      }
    },
    [preferredDestinationRef, runOperator],
  )

  const patchInputs = useCallback((patch: Partial<OperationInputs>) => {
    const next = { ...draftInputsRef.current, ...patch }
    draftInputsRef.current = next
    setDraftInputs(next)
    lastAutoRunKeyRef.current = ''
  }, [])

  /** Selecting a step-result destination (Enter) runs immediately so it can chain a next step. */
  const selectDestinationEntity = useCallback(
    (id: string) => {
      const next = {
        ...draftInputsRef.current,
        destinationMode: 'entity' as const,
        destinationEntityRef: id,
        destinationAddress: '',
      }
      patchInputs({
        destinationMode: 'entity',
        destinationEntityRef: id,
        destinationAddress: '',
      })
      const op = activeOperatorRef.current
      if (
        op &&
        parseStepPointRef(id) &&
        areOperatorArgsComplete(op, next) &&
        !operationRunning
      ) {
        runOperator(op, next)
      }
    },
    [operationRunning, patchInputs, runOperator],
  )

  const runActiveOperator = useCallback(() => {
    const op = activeOperatorRef.current
    if (!op) return
    runOperator(op, draftInputsRef.current)
  }, [runOperator])

  const destinationOptional = activeOperatorId === 'fuel-planning'
  const needsDestination = activeOperator?.args.includes('destination') ?? false
  // Keep this popup open while configuring a destination so pins can be dragged into the field.
  const lockPopupForEntityDrop = Boolean(activeOperator) && needsDestination
  const argsNeedManualRun = (activeOperator?.args.length ?? 0) > 0
  const canManualRun =
    Boolean(activeOperator) &&
    areOperatorArgsComplete(activeOperator!, draftInputs) &&
    !operationRunning
  const showRunBesideRadius = Boolean(activeOperator?.args.includes('radiusMiles'))
  const showRunBesideRange =
    !showRunBesideRadius && Boolean(activeOperator?.args.includes('rangeMiles'))
  const showRunAtEnd =
    argsNeedManualRun && !showRunBesideRadius && !showRunBesideRange
  const isRerun =
    Boolean(activeOperator) && activeOperatorId === operationResultOperatorId
  const runLabel = `${isRerun ? 'Re-run' : 'Run'} ${activeOperator?.label ?? 'operation'}`

  return {
    activeOperatorId,
    activeOperator,
    draftInputs,
    popupDismissed,
    lockPopupForEntityDrop,
    dismissPopup,
    revealPopup,
    selectOperator,
    patchInputs,
    selectDestinationEntity,
    runActiveOperator,
    destinationOptional,
    needsDestination,
    canManualRun,
    showRunBesideRadius,
    showRunBesideRange,
    showRunAtEnd,
    isRerun,
    runLabel,
  }
}

export type OperatorPopupControlsProps = {
  variant: OperatorPopupVariant
  operators: OperatorDef[]
  operationRunning?: boolean
  destinationOptions?: OperatorPopupDestinationOption[]
  activeOperatorId: OperatorId | null
  activeOperator: OperatorDef | null
  draftInputs: OperationInputs
  selectOperator: (op: OperatorDef) => void
  patchInputs: (patch: Partial<OperationInputs>) => void
  selectDestinationEntity: (id: string) => void
  runActiveOperator: () => void
  destinationOptional: boolean
  needsDestination: boolean
  canManualRun: boolean
  showRunBesideRadius: boolean
  showRunBesideRange: boolean
  showRunAtEnd: boolean
  isRerun: boolean
  runLabel: string
}

export function OperatorPopupControls({
  variant,
  operators,
  operationRunning = false,
  destinationOptions = [],
  activeOperatorId,
  activeOperator,
  draftInputs,
  selectOperator,
  patchInputs,
  selectDestinationEntity,
  runActiveOperator,
  destinationOptional,
  needsDestination,
  canManualRun,
  showRunBesideRadius,
  showRunBesideRange,
  showRunAtEnd,
  isRerun,
  runLabel,
  embedded = false,
}: OperatorPopupControlsProps & { embedded?: boolean }) {
  const manualRunButton = activeOperator ? (
    <button
      type="button"
      className={`btn btn-primary btn-square shrink-0 ${embedded ? 'h-11 min-h-11 w-11' : 'btn-sm'}`}
      disabled={!canManualRun}
      aria-label={runLabel}
      title={runLabel}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        runActiveOperator()
      }}
    >
      <FontAwesomeIcon
        icon={
          operationRunning && activeOperatorId === activeOperator.id
            ? faSpinner
            : isRerun
              ? faRotateRight
              : faPlay
        }
        className={
          operationRunning && activeOperatorId === activeOperator.id
            ? 'animate-spin'
            : embedded
              ? 'text-sm'
              : ''
        }
      />
    </button>
  ) : null

  return (
    <div className={embedded ? '' : 'mt-2 border-t border-base-200 pt-2'}>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
        Operations
      </div>
      <div className={`flex flex-wrap ${embedded ? 'gap-2' : 'gap-1'}`}>
        {operators.map((op) => (
          <div key={op.id} className="tooltip tooltip-top" data-tip={op.label}>
            <button
              type="button"
              className={`btn btn-ghost btn-square border ${
                embedded ? 'h-11 min-h-11 w-11' : 'btn-xs'
              } ${
                activeOperatorId === op.id
                  ? 'border-primary bg-primary/10'
                  : 'border-base-300'
              }`}
              aria-label={op.label}
              aria-pressed={activeOperatorId === op.id}
              disabled={operationRunning}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                selectOperator(op)
              }}
            >
              <FontAwesomeIcon
                icon={OPERATOR_ICON[op.id]}
                className={embedded ? 'text-sm' : 'text-xs'}
              />
            </button>
          </div>
        ))}
      </div>

      {activeOperator && activeOperator.args.length > 0 ? (
        <div className="mt-2 space-y-2 border-t border-base-200 pt-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
            {activeOperator.label} arguments
          </div>
          {activeOperator.args.includes('nearbyTarget') ? (
            <div className="form-control w-full">
              <div className="label py-0 pb-1">
                <span className="label-text text-[11px]">Find nearby</span>
              </div>
              <EntitySearch
                items={NEARBY_TARGET_OPTIONS}
                value={draftInputs.nearbyTarget}
                onChange={(id) =>
                  patchInputs({
                    nearbyTarget: id as NearbyTarget,
                  })
                }
                placeholder="Search layers or establishments…"
                emptyHint="No matching targets."
                aria-label="Find nearby target"
                allowClear
              />
            </div>
          ) : null}
          {activeOperator.args.includes('radiusMiles') ? (
            <div className="flex items-end gap-1.5">
              <label className="form-control min-w-0 flex-1">
                <div className="label py-0 pb-1">
                  <span className="label-text text-[11px]">Radius (mi)</span>
                </div>
                <input
                  type="number"
                  min={1}
                  max={200}
                  className="input input-bordered input-sm w-full"
                  value={draftInputs.radiusMiles}
                  onChange={(e) =>
                    patchInputs({
                      radiusMiles: Number(e.target.value) || 0,
                    })
                  }
                />
              </label>
              {showRunBesideRadius ? manualRunButton : null}
            </div>
          ) : null}
          {variant === 'entity' && activeOperator.args.includes('rangeMiles') ? (
            <div className="flex items-end gap-1.5">
              <label className="form-control min-w-0 flex-1">
                <div className="label py-0 pb-1">
                  <span className="label-text text-[11px]">Range (mi)</span>
                </div>
                <input
                  type="number"
                  min={10}
                  max={500}
                  className="input input-bordered input-sm w-full"
                  value={draftInputs.rangeMiles}
                  onChange={(e) =>
                    patchInputs({
                      rangeMiles: Number(e.target.value) || 0,
                    })
                  }
                />
              </label>
              {showRunBesideRange ? manualRunButton : null}
            </div>
          ) : null}
          {variant === 'entity' && activeOperator.args.includes('forecastType') ? (
            <label className="form-control w-full">
              <div className="label py-0 pb-1">
                <span className="label-text text-[11px]">Activity type</span>
              </div>
              <select
                className="select select-bordered select-sm w-full"
                value={draftInputs.forecastType}
                onChange={(e) =>
                  patchInputs({
                    forecastType: e.target.value as ForecastType,
                  })
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="starting">Starting</option>
                <option value="ending">Ending</option>
              </select>
            </label>
          ) : null}
          {variant === 'entity' &&
          (activeOperator.args.includes('forecastStartDate') ||
            activeOperator.args.includes('forecastEndDate')) ? (
            <ForecastDateRangePicker
              startDate={draftInputs.forecastStartDate}
              endDate={draftInputs.forecastEndDate}
              onChange={({ startDate, endDate }) =>
                patchInputs({
                  forecastStartDate: startDate,
                  forecastEndDate: endDate,
                })
              }
            />
          ) : null}
          {variant === 'entity' &&
          (activeOperator.args.includes('timeStartDate') ||
            activeOperator.args.includes('timeEndDate')) ? (
            <TimeRangeArgsFields
              startDate={draftInputs.timeStartDate}
              endDate={draftInputs.timeEndDate}
              onChange={({ timeStartDate, timeEndDate }) =>
                patchInputs({ timeStartDate, timeEndDate })
              }
            />
          ) : null}
          {needsDestination ? (
            variant === 'entity' ? (
              <div className="space-y-2">
                <label className="form-control w-full">
                  <div className="label py-0 pb-1">
                    <span className="label-text text-[11px]">
                      Destination mode{destinationOptional ? ' (optional)' : ''}
                    </span>
                  </div>
                  <select
                    className="select select-bordered select-sm w-full"
                    value={draftInputs.destinationMode}
                    onChange={(e) =>
                      patchInputs({
                        destinationMode:
                          e.target.value === 'address' ? 'address' : 'entity',
                      })
                    }
                  >
                    <option value="entity">Entity</option>
                    <option value="address">Address</option>
                  </select>
                </label>
                {draftInputs.destinationMode === 'address' ? (
                  <label className="form-control w-full">
                    <div className="label py-0 pb-1">
                      <span className="label-text text-[11px]">
                        Destination address{destinationOptional ? ' (optional)' : ''}
                      </span>
                    </div>
                    <input
                      type="text"
                      className="input input-bordered input-sm w-full"
                      value={draftInputs.destinationAddress}
                      onChange={(e) =>
                        patchInputs({
                          destinationAddress: e.target.value,
                        })
                      }
                      placeholder="Street, city…"
                    />
                  </label>
                ) : (
                  <div className="form-control w-full">
                    <div className="label py-0 pb-1">
                      <span className="label-text text-[11px]">
                        Destination entity{destinationOptional ? ' (optional)' : ''}
                      </span>
                    </div>
                    <EntitySearch
                      items={destinationOptions}
                      value={draftInputs.destinationEntityRef}
                      onChange={selectDestinationEntity}
                      placeholder="Search destinations…"
                      emptyHint="No matching entities."
                      aria-label={
                        destinationOptional
                          ? 'Destination entity (optional)'
                          : 'Destination entity'
                      }
                      allowClear={destinationOptional}
                      acceptEntityDrop
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-[11px]">
                    Destination{destinationOptional ? ' (optional)' : ''}
                  </span>
                </div>
                <EntitySearch
                  items={destinationOptions}
                  value={draftInputs.destinationEntityRef}
                  onChange={selectDestinationEntity}
                  placeholder="Search destination…"
                  emptyHint="No matching destinations."
                  aria-label="Destination"
                  allowClear
                  acceptEntityDrop
                />
              </div>
            )
          ) : null}
          {showRunAtEnd ? (
            <div className="flex items-center justify-end gap-2 pt-0.5">
              {!areOperatorArgsComplete(activeOperator!, draftInputs) ? (
                <p className="mr-auto text-[11px] text-base-content/55">
                  Complete required arguments to run.
                </p>
              ) : null}
              {manualRunButton}
            </div>
          ) : null}
          {operationRunning && activeOperatorId ? (
            <p className="flex items-center gap-1.5 text-[11px] text-base-content/60">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              Running {activeOperator.label}…
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** Self-contained ops UI for the output panel (mobile: replaces map popups). */
export function PanelOperatorSection({
  variant,
  operators,
  operationRunning = false,
  destinationOptions = [],
  preferredDestinationRef = '',
  operationResultOperatorId = null,
  openFocusToken = 0,
  onRun,
}: {
  variant: OperatorPopupVariant
  operators: OperatorDef[]
  operationRunning?: boolean
  destinationOptions?: OperatorPopupDestinationOption[]
  preferredDestinationRef?: string
  operationResultOperatorId?: OperatorId | null
  openFocusToken?: number
  onRun: (op: OperatorDef, inputs: OperationInputs) => void
}) {
  const state = useOperatorPopupState({
    operators,
    preferredDestinationRef,
    operationResultOperatorId,
    operationRunning,
    isActive: true,
    openPopupToken: openFocusToken,
    onRun,
  })

  if (operators.length === 0) return null

  return (
    <OperatorPopupControls
      variant={variant}
      operators={operators}
      operationRunning={operationRunning}
      destinationOptions={destinationOptions}
      activeOperatorId={state.activeOperatorId}
      activeOperator={state.activeOperator}
      draftInputs={state.draftInputs}
      selectOperator={state.selectOperator}
      patchInputs={state.patchInputs}
      selectDestinationEntity={state.selectDestinationEntity}
      runActiveOperator={state.runActiveOperator}
      destinationOptional={state.destinationOptional}
      needsDestination={state.needsDestination}
      canManualRun={state.canManualRun}
      showRunBesideRadius={state.showRunBesideRadius}
      showRunBesideRange={state.showRunBesideRange}
      showRunAtEnd={state.showRunAtEnd}
      isRerun={state.isRerun}
      runLabel={state.runLabel}
      embedded
    />
  )
}

